import React from 'react';
import PropTypes from 'prop-types';
import { withNamespaces, Trans } from 'react-i18next';
import { observer } from 'mobx-react';
import Heading from 'components/Heading';
import Paragraph from 'components/Paragraph';
import Button from 'components/Button';
import AddressCard from 'apps/addresses/blocks/AddressCard';
import { confToFields } from 'libs/forms';

import SupportingFiles from 'apps/rfq/rfqPage/views/supportingFiles';

import {
  LargePanelSectionWrapper,
  ContentBlockWrapper,
  ContentBlock,
  TitleRowWrapper,
} from 'libs/rendering/gltf/components/largePanel/styles';

const DetailsBlock = observer(props => {
  const { jobSpec, rfqManager, dataTestIdPrefix = '' } = props;
  const { quantity, build } = jobSpec;
  const { address } = rfqManager.rfqApp;

  const { process, material } = build;

  const editAction = false; // FIX.

  function valKey(valueType) {
    return `value${valueType.charAt(0)}${valueType.slice(1).toLowerCase()}`;
  }
  let { values } = jobSpec.build;
  if (!values) {
    values = []; // server seems to return null for build.values when it is empty.
  }
  const buildVals = values.map(buildVal => {
    const { valueBool, of } = buildVal;
    if (valueBool === false) {
      return null;
    }

    const { name, unit, valueType, nilLabel, prefix } = of;
    const valueKey = valKey(valueType);
    let bVal = buildVal[valueKey];
    if (bVal === null) {
      bVal = nilLabel;
    }
    if (unit) {
      bVal += ` ${unit}`;
    }
    if (prefix) {
      bVal = `${prefix}${bVal}`;
    }
    if (bVal === true) {
      bVal = '';
    }
    return (
      <ContentBlock key={name} empty={false}>
        <Heading level='5' size='small' key={name}>
          {name}
          {valueBool === true && <> ✓</>}
        </Heading>
        <Paragraph>{bVal}</Paragraph>
      </ContentBlock>
    );
  });

  const { leadTime } = jobSpec;

  let supportingFiles = null;
  let addressField = null;
  // This happens in OrderPage. We have no rfqManager, but we have the files in the jobSpec.
  if (!rfqManager) {
    supportingFiles = jobSpec?.build?.supportingFiles;
  } else {
    const { updateAddressFormStore } = rfqManager;
    addressField = confToFields({
      formStore: updateAddressFormStore,
      refs: rfqManager.addressRefs,
      responsive: true,
    });
  }

  const addressCard = <AddressCard address={address} width='50%' singleCard />;

  const isAddressEditable = rfqManager?.rfqApp?.isUpdatable;

  const shippingAddressDisplay = (
    <ContentBlock key='delivery-country' empty={false}>
      <Heading level='5' size='small'>
        Delivery Address
      </Heading>
      {address && addressCard}
      {isAddressEditable && <div>{isAddressEditable && addressField}</div>}
    </ContentBlock>
  );

  return (
    <>
      <LargePanelSectionWrapper first>
        <TitleRowWrapper>
          <Heading level='2' size='large' margin='0 0 large 0'>
            <Trans>Part Details</Trans>
          </Heading>
          {editAction && (
            <Button
              text={<Trans>Edit</Trans>}
              appearance='plain'
              margin='0 0 0 small'
              onClick={editAction}
            />
          )}
        </TitleRowWrapper>
        <ContentBlockWrapper>
          <ContentBlock key='fab-process' empty={false}>
            <Heading level='5' size='small'>
              Fabrication Process
            </Heading>
            <Paragraph data-testid={`${dataTestIdPrefix}process`}>
              {process.name}
              {process.parentProcess && <>, {process.parentProcess.name}</>}
            </Paragraph>
          </ContentBlock>
          <ContentBlock key='material' empty={false}>
            <Heading level='5' size='small'>
              Material
            </Heading>
            <Paragraph data-testid={`${dataTestIdPrefix}material`}>
              {material.name}
              {material.parentMaterial && <>, {material.parentMaterial.name}</>}
            </Paragraph>
          </ContentBlock>
          {buildVals}
        </ContentBlockWrapper>
      </LargePanelSectionWrapper>
      {rfqManager ? (
        <LargePanelSectionWrapper>
          <SupportingFiles rfqManager={rfqManager} />
        </LargePanelSectionWrapper>
      ) : supportingFiles ? (
        <LargePanelSectionWrapper>
          <SupportingFiles
            supportingFiles={supportingFiles}
            canUpdateFiles={false}
          />
        </LargePanelSectionWrapper>
      ) : null}
      <LargePanelSectionWrapper>
        <TitleRowWrapper>
          <Heading level='2' size='large' margin='0 0 large 0'>
            <Trans>Quote Details</Trans>
          </Heading>
          {editAction && (
            <Button
              text={<Trans>Edit</Trans>}
              appearance='plain'
              margin='0 0 0 small'
              onClick={editAction}
            />
          )}
        </TitleRowWrapper>
        <ContentBlockWrapper>
          <ContentBlock key='quantity' empty={false}>
            <Heading level='5' size='small'>
              Quantity
            </Heading>
            <Paragraph data-testid={`${dataTestIdPrefix}quantity`}>
              {quantity}
            </Paragraph>
          </ContentBlock>
          {leadTime && (
            <ContentBlock key='lead-time' empty={false}>
              <Heading level='5' size='small'>
                Manufacturing Time
              </Heading>
              <Paragraph data-testid={`${dataTestIdPrefix}leadTime`}>
                {leadTime} Business Days
              </Paragraph>
            </ContentBlock>
          )}
          {shippingAddressDisplay}
        </ContentBlockWrapper>
      </LargePanelSectionWrapper>
    </>
  );
});

DetailsBlock.propTypes = {
  jobSpec: PropTypes.object,
  rfqManager: PropTypes.object,
};

export default withNamespaces()(DetailsBlock);
